import React, { useState, useEffect, useCallback, useRef, startTransition } from 'react';
import { useNavigate, useLocation, Routes, Route } from 'react-router-dom';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  Divider,
  Badge,
  Tooltip,
  useTheme,
  useMediaQuery,
  alpha,
  Input,
  Paper,
  Popper,
  ClickAwayListener,
  List,
  ListItemText,
  CircularProgress,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Notifications as NotificationsIcon,
  AccountCircle as AccountCircleIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  Search as SearchIcon,
  Dashboard as DashboardIcon,
  Inventory as InventoryIcon,
  People as PeopleIcon,
  ShoppingCart as SalesIcon,
  Build as BuildIcon,
  AddShoppingCart as AddCartIcon,
  Mic as MicIcon,
  Receipt as ReceiptIcon,
  SupportAgent as SupportAgentIcon,
  Close as CloseIcon,
  ArrowBack as ArrowBackIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  MenuOpen as MenuOpenIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContextMongo';
import { Navigation } from './Navigation';
import NotificacionesModal from '../common/NotificacionesModal';
import SoporteTecnicoModal from '../SoporteTecnicoModal';
import ProductDetailModal from '../products/ProductDetailModal';
import PrintQueueMonitor from '../print/PrintQueueMonitor';
import { useBusiness } from '../../context/BusinessContext';
import api from '../../api/api';
import CanvaFlyerGenerator from '../canva/CanvaFlyerGenerator';
import { useVoiceAssistant } from '../../hooks/useVoiceAssistant';
import { useCart } from '../../context/CartContext';
import { useSnackbar } from 'notistack';

// ─── Pre-order confirmation dialog ─────────────────────────────────────────
const PreOrderDialog = ({ product, open, onClose, onAddToCart, onNavigate }) => {
  if (!product) return null;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 4 } }}>
      <DialogTitle sx={{ fontWeight: 800, pb: 0 }}>
        ¿Qué hacemos con este producto?
      </DialogTitle>
      <DialogContent sx={{ pt: 1.5 }}>
        <Typography variant="body2" color="text.secondary" mb={2}>
          <strong>{product.nombre}</strong> — RD$ {product.precio}
        </Typography>
        <Stack spacing={1.5}>
          <Button
            fullWidth
            variant="contained"
            startIcon={<AddCartIcon />}
            onClick={() => { onAddToCart(product); onClose(); }}
            sx={{ borderRadius: 3, textTransform: 'none', fontWeight: 700, background: 'linear-gradient(135deg,#6b46c1,#319795)' }}
          >
            Agregar al carrito e ir al POS
          </Button>
          <Button
            fullWidth
            variant="outlined"
            startIcon={<InventoryIcon />}
            onClick={() => { onNavigate(`/products`); onClose(); }}
            sx={{ borderRadius: 3, textTransform: 'none', fontWeight: 700 }}
          >
            Ver detalle del producto
          </Button>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', color: 'text.secondary' }}>Cancelar</Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Main Layout ─────────────────────────────────────────────────────────────
const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const { businessData } = useBusiness();
  const { addToCart } = useCart();
  const { enqueueSnackbar } = useSnackbar();
  const voiceAssistant = useVoiceAssistant();

  const [notificacionesNoLeidas, setNotificacionesNoLeidas] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);

  const [anchorEl, setAnchorEl] = useState(null);
  const [openNotificaciones, setOpenNotificaciones] = useState(false);
  const [openSoporte, setOpenSoporte] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState({ products: [], customers: [], repairs: [] });
  const [isSearching, setIsSearching] = useState(false);
  const [searchAnchorEl, setSearchAnchorEl] = useState(null);
  const [navigationResults, setNavigationResults] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [preOrderProduct, setPreOrderProduct] = useState(null);
  const [preOrderOpen, setPreOrderOpen] = useState(false);
  const [mobileSearchActive, setMobileSearchActive] = useState(false);

  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Sidebar collapsed state (mini rail 72px vs 260px)
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem('posent_sidebar_collapsed') === 'true';
    } catch {
      return false;
    }
  });

  const toggleSidebarCollapse = useCallback(() => {
    setIsSidebarCollapsed(prev => {
      const next = !prev;
      try {
        localStorage.setItem('posent_sidebar_collapsed', String(next));
      } catch {}
      return next;
    });
  }, []);

  // Per-page fullscreen or temporary fullscreen state
  const [fullscreenPages, setFullscreenPages] = useState(() => {
    try {
      const saved = localStorage.getItem('posent_fullscreen_pages');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  const [tempFullscreen, setTempFullscreen] = useState(false);

  // Reset temp fullscreen on route change
  useEffect(() => {
    setTempFullscreen(false);
  }, [location.pathname]);

  const isDefaultAppMode = ['/quick-sale', '/pos'].includes(location.pathname);
  const isRouteFullscreen = fullscreenPages.includes(location.pathname);
  const isAppMode = isDefaultAppMode || isRouteFullscreen || tempFullscreen;

  const togglePageFullscreen = useCallback((permanent = false) => {
    if (isDefaultAppMode) return;
    if (permanent) {
      setFullscreenPages(prev => {
        const next = prev.includes(location.pathname)
          ? prev.filter(p => p !== location.pathname)
          : [...prev, location.pathname];
        try {
          localStorage.setItem('posent_fullscreen_pages', JSON.stringify(next));
        } catch {}
        return next;
      });
    } else {
      setTempFullscreen(prev => !prev);
    }
  }, [isDefaultAppMode, location.pathname]);

  // Keyboard shortcuts (Ctrl+B = toggle sidebar, F11 or Ctrl+Shift+F = toggle fullscreen)
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ctrl+B to toggle sidebar collapse
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'b') {
        e.preventDefault();
        toggleSidebarCollapse();
      }
      // F11 or Ctrl+Shift+F to toggle POS fullscreen
      if (e.key === 'F11' || ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === 'f')) {
        e.preventDefault();
        togglePageFullscreen();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [toggleSidebarCollapse, togglePageFullscreen]);

  const drawerFullWidth = 260;
  const drawerCollapsedWidth = 72;
  const activeDrawerWidth = isAppMode ? 0 : (isSidebarCollapsed ? drawerCollapsedWidth : drawerFullWidth);
  const searchDebounceRef = useRef(null);

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  useEffect(() => {
    setNotificacionesNoLeidas(0);
  }, [user]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
    }
  };

  const handleProfile = () => {
    handleMenuClose();
    navigate('/profile');
  };

  // ── Add product to cart and navigate to POS ──────────────────────────────
  const handleAddToCart = useCallback((p) => {
    const productForCart = {
      id: p._id || p.id,
      name: p.nombre || p.name,
      price: parseFloat(p.precio || p.price || 0),
      quantity: 1,
      code: p.codigo || p.code || '',
      meta: { type: 'product', source: 'header_search' },
    };
    addToCart(productForCart);
    enqueueSnackbar(`${p.nombre || p.name} agregado al carrito ✅`, { variant: 'success' });
    setSearchQuery('');
    setSearchAnchorEl(null);
    navigate('/quick-sale');
  }, [addToCart, enqueueSnackbar, navigate]);

  // ── Navigation shortcuts ─────────────────────────────────────────────────
  const NAV_ITEMS = [
    { title: 'Dashboard / Inicio 🏠', path: '/dashboard', keywords: ['dashboard','inicio','inicio','home','resumen'] },
    { title: 'Inventario Pro 📊', path: '/inventory', keywords: ['inventario','stock','almacen','kardex','existencias','ajuste','reabastecer','inventario pro'] },
    { title: 'Productos / Catálogo 📦', path: '/products', keywords: ['productos','producto','catalogo','precio','crear producto'] },
    { title: 'Nueva Venta / POS 🛒', path: '/quick-sale', keywords: ['venta','pos','cobrar','cobro','caja','ventas','nueva venta'] },
    { title: 'Clientes 👥', path: '/customers', keywords: ['clientes','cliente','contacto','contactos'] },
    { title: 'Reparaciones 🔧', path: '/reparaciones', keywords: ['reparacion','reparaciones','taller','servicio tecnico'] },
    { title: 'Contabilidad 📊', path: '/contabilidad', keywords: ['contabilidad','finanzas','cuentas'] },
    { title: 'Proveedores 🚚', path: '/suppliers', keywords: ['proveedores','proveedor','supplier'] },
    { title: 'Suscripción 💳', path: '/subscriptions', keywords: ['suscripcion','plan','membresia'] },
    { title: 'Mi Cuenta 👤', path: '/perfil', keywords: ['perfil','cuenta','usuario'] },
    { title: 'Configuración / Ajustes ⚙️', path: '/settings', keywords: ['configuracion','ajustes','settings','configurar'] },
    { title: 'Analíticas 📈', path: '/analytics', keywords: ['analitica','analiticas','estadisticas','reportes','metricas'] },
    { title: 'Facturación 🧾', path: '/billing', keywords: ['factura','facturacion','factura electronica'] },
    { title: 'Promociones 🎁', path: '/promotions', keywords: ['promociones','promocion','descuento'] },
  ];

  // ── Search ───────────────────────────────────────────────────────────────
  const handleSearch = useCallback((query) => {
    setSearchQuery(query);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);

    if (query.length < 2) {
      setSearchResults({ products: [], customers: [], repairs: [] });
      setNavigationResults([]);
      return;
    }

    // Instant navigation results (local, no API)
    const normalized = query.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    const navMatches = NAV_ITEMS.filter(item =>
      item.keywords.some(kw => kw.includes(normalized)) ||
      item.title.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').includes(normalized)
    );
    setNavigationResults(navMatches);

    searchDebounceRef.current = setTimeout(async () => {
      setIsSearching(true);
      try {
        // Use the dedicated /search routes that exist in the backend
        const [prodRes, custRes] = await Promise.all([
          api.get(`/search/products?q=${encodeURIComponent(query)}&limit=6`).catch(() => ({ data: { data: [] } })),
          api.get(`/search/customers?q=${encodeURIComponent(query)}&limit=4`).catch(() => ({ data: { data: [] } })),
        ]);
        setSearchResults({
          products: prodRes.data?.data || [],
          customers: custRes.data?.data || [],
          repairs: [],
        });
      } catch (err) {
        console.error('Search error:', err);
      } finally {
        setIsSearching(false);
      }
    }, 280);
  }, []);

  const handleResultClick = (path) => {
    setSearchAnchorEl(null);
    setSearchQuery('');
    startTransition(() => navigate(path));
  };

  const handleProductClick = (p) => {
    setSearchAnchorEl(null);
    setPreOrderProduct(p);
    setPreOrderOpen(true);
  };

  const handleVoiceMicClick = () => {
    setSearchAnchorEl(null);
    setSearchQuery('');
    voiceAssistant.openAssistant({ startListening: true });
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', width: '100%', maxWidth: '100vw', overflowX: 'hidden', bgcolor: 'background.default' }}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { xs: '100%', md: isAppMode ? '100%' : `calc(100% - ${activeDrawerWidth}px)` },
          left: { xs: 0, md: isAppMode ? 0 : `${activeDrawerWidth}px` },
          top: { xs: 'auto', md: 0 },
          bottom: { xs: 0, md: 'auto' },
          zIndex: (theme) => theme.zIndex.appBar, // 1100 (Below Drawer/Modal 1200/1300)
          background: alpha(theme.palette.background.paper, 0.94),
          backdropFilter: 'blur(20px)',
          color: theme.palette.text.primary,
          borderBottom: { xs: 'none', md: `1px solid ${alpha(theme.palette.divider, 0.08)}` },
          borderTop: { xs: `1px solid ${alpha(theme.palette.divider, 0.12)}`, md: 'none' },
          boxShadow: { xs: '0 -4px 20px rgba(0,0,0,0.08)', md: 'none' },
          transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
          paddingTop: { xs: 0, md: 'env(safe-area-inset-top, 0px)' },
          paddingBottom: { xs: 'env(safe-area-inset-bottom, 0px)', md: 0 },
        }}
      >
        <Toolbar sx={{
          minHeight: { xs: '58px !important', md: '80px' },
          height: { xs: '58px', md: 'auto' },
          px: { xs: 1.5, sm: 2, md: 3 },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
          {isMobile && mobileSearchActive ? (
            /* ── Mobile Search Mode ── */
            <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton
                onClick={() => {
                  setMobileSearchActive(false);
                  setSearchQuery('');
                  setSearchAnchorEl(null);
                }}
                sx={{
                  color: 'primary.main',
                  backgroundColor: alpha(theme.palette.primary.main, 0.08),
                  borderRadius: '12px',
                  width: 40,
                  height: 40,
                  '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.15) },
                }}
              >
                <CloseIcon />
              </IconButton>
              <Box
                sx={{
                  flexGrow: 1,
                  display: 'flex',
                  alignItems: 'center',
                  bgcolor: alpha(theme.palette.action.hover, 0.05),
                  borderRadius: '50px',
                  px: 2,
                  py: 1,
                  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                }}
              >
                <SearchIcon sx={{ color: 'text.secondary', mr: 1, fontSize: 20 }} />
                <Input
                  placeholder="Buscar productos, clientes, ajustes... 🔎"
                  disableUnderline
                  fullWidth
                  value={searchQuery}
                  onChange={(e) => {
                    handleSearch(e.target.value);
                    setSearchAnchorEl(e.currentTarget.parentElement);
                  }}
                  onFocus={(e) => {
                    if (searchQuery.length >= 2) setSearchAnchorEl(e.currentTarget.parentElement);
                  }}
                  autoFocus
                  sx={{ fontSize: '0.9rem', fontWeight: 600, color: 'text.primary' }}
                />
                <IconButton
                  size="small"
                  onClick={handleVoiceMicClick}
                  sx={{
                    ml: 0.5,
                    color: voiceAssistant.status === 'listening' ? 'error.main' : 'primary.main',
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    width: 32,
                    height: 32,
                    '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.18) },
                  }}
                >
                  <MicIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
            </Box>
          ) : (
            /* ── Normal Toolbar Content ── */
            <>
              {/* ── Botón en esquina izquierda según modo ── */}
              {isAppMode ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: { xs: 1, md: 2 } }}>
                  {isDefaultAppMode ? (
                    <Tooltip title="Volver al Dashboard" arrow>
                      <Button
                        onClick={() => startTransition(() => navigate('/dashboard'))}
                        startIcon={<ArrowBackIcon />}
                        sx={{
                          borderRadius: '12px',
                          bgcolor: alpha(theme.palette.primary.main, 0.08),
                          color: 'primary.main',
                          fontWeight: 700,
                          textTransform: 'none',
                          fontSize: '0.85rem',
                          px: { xs: 1.2, sm: 1.8 },
                          py: 0.75,
                          transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.16),
                            transform: 'translateX(-3px)',
                          },
                        }}
                      >
                        Dashboard
                      </Button>
                    </Tooltip>
                  ) : (
                    <Tooltip title="Salir de Pantalla Completa" arrow>
                      <Button
                        onClick={() => togglePageFullscreen()}
                        startIcon={<FullscreenExitIcon />}
                        sx={{
                          borderRadius: '12px',
                          bgcolor: alpha(theme.palette.primary.main, 0.08),
                          color: 'primary.main',
                          fontWeight: 700,
                          textTransform: 'none',
                          fontSize: '0.82rem',
                          px: 1.4,
                          py: 0.7,
                          '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.16) }
                        }}
                      >
                        Modo Normal
                      </Button>
                    </Tooltip>
                  )}

                  <Tooltip title="Abrir Menú Completo" arrow>
                    <IconButton
                      aria-label="abrir menú"
                      onClick={handleDrawerToggle}
                      sx={{
                        color: 'text.secondary',
                        backgroundColor: alpha(theme.palette.action.hover, 0.06),
                        borderRadius: '12px',
                        width: 38,
                        height: 38,
                        '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.12), color: 'primary.main' },
                      }}
                    >
                      <MenuIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              ) : (
                <>
                  {isMobile ? (
                    <IconButton
                      aria-label="open drawer"
                      edge="start"
                      onClick={handleDrawerToggle}
                      sx={{
                        mr: 1.5,
                        color: 'primary.main',
                        backgroundColor: alpha(theme.palette.primary.main, 0.08),
                        borderRadius: '12px',
                        '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.15) },
                      }}
                    >
                      <MenuIcon />
                    </IconButton>
                  ) : (
                    <Tooltip title={isSidebarCollapsed ? "Expandir menú (Ctrl+B)" : "Colapsar menú (Modo Compacto Ctrl+B)"} arrow>
                      <IconButton
                        aria-label="colapsar o expandir menú"
                        onClick={toggleSidebarCollapse}
                        sx={{
                          mr: 1.5,
                          color: isSidebarCollapsed ? 'primary.main' : 'text.secondary',
                          backgroundColor: isSidebarCollapsed ? alpha(theme.palette.primary.main, 0.1) : alpha(theme.palette.action.hover, 0.06),
                          borderRadius: '12px',
                          width: 40,
                          height: 40,
                          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.15),
                            color: 'primary.main',
                            transform: 'scale(1.05)',
                          },
                        }}
                      >
                        {isSidebarCollapsed ? <MenuOpenIcon sx={{ fontSize: 22 }} /> : <MenuIcon sx={{ fontSize: 22 }} />}
                      </IconButton>
                    </Tooltip>
                  )}
                </>
              )}

              {/* ── Search bar (Desktop) ── */}
              <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' }, alignItems: 'center', position: 'relative', maxWidth: 680 }}>
                <Box
                  sx={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    bgcolor: alpha(theme.palette.action.hover, 0.05),
                    borderRadius: '50px',
                    px: 2,
                    py: 1.2,
                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    transition: 'all 0.3s ease',
                    '&:hover': { bgcolor: alpha(theme.palette.action.hover, 0.1) },
                    '&:focus-within': {
                      bgcolor: 'background.paper',
                      borderColor: 'primary.main',
                      boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.15)}`,
                    },
                  }}
                >
                  <SearchIcon sx={{ color: 'text.secondary', mr: 1.5, fontSize: 22, flexShrink: 0 }} />
                  <Input
                    placeholder="Busca productos, clientes, ajustes... 🔎"
                    disableUnderline
                    value={searchQuery}
                    onChange={(e) => {
                      handleSearch(e.target.value);
                      setSearchAnchorEl(e.currentTarget.parentElement);
                    }}
                    onFocus={(e) => {
                      if (searchQuery.length >= 2) setSearchAnchorEl(e.currentTarget.parentElement);
                    }}
                    sx={{ width: '100%', fontSize: '0.92rem', fontWeight: 600, color: 'text.primary' }}
                  />

                  {/* Keyboard hint */}
                  <Box sx={{ px: 1, py: 0.3, borderRadius: 2, bgcolor: alpha(theme.palette.primary.main, 0.08), display: { xs: 'none', lg: 'flex' }, alignItems: 'center', gap: 1, flexShrink: 0 }}>
                    <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 800 }}>Ctrl+K</Typography>
                  </Box>

                  {/* Mic icon → opens voice assistant */}
                  <Tooltip title="Buscar por voz 🎙" arrow>
                    <IconButton
                      size="small"
                      onClick={handleVoiceMicClick}
                      sx={{
                        ml: 0.5,
                        flexShrink: 0,
                        color: voiceAssistant.status === 'listening' ? 'error.main' : 'primary.main',
                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                        width: 34,
                        height: 34,
                        '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.18) },
                        ...(voiceAssistant.status === 'listening' && {
                          animation: 'micPulse 1s ease-in-out infinite',
                          '@keyframes micPulse': {
                            '0%': { boxShadow: '0 0 0 0 rgba(229,62,62,0.5)' },
                            '70%': { boxShadow: '0 0 0 10px rgba(229,62,62,0)' },
                            '100%': { boxShadow: '0 0 0 0 rgba(229,62,62,0)' },
                          },
                        }),
                      }}
                    >
                      <MicIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              <Box sx={{ flexGrow: 1 }} />

              <Stack direction="row" spacing={{ xs: 0.5, md: 1.5 }} alignItems="center">
                {user && (
                  <>
                    {/* Search and Mic buttons for mobile to toggle Search Mode / Voice Assistant */}
                    {isMobile && (
                      <>
                        <IconButton
                          onClick={() => setMobileSearchActive(true)}
                          sx={{
                            color: 'text.secondary',
                            bgcolor: alpha(theme.palette.action.hover, 0.05),
                            width: 38,
                            height: 38,
                            borderRadius: '10px',
                            '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.08), color: 'primary.main' },
                          }}
                        >
                          <SearchIcon sx={{ fontSize: 20 }} />
                        </IconButton>
                        <IconButton
                          onClick={handleVoiceMicClick}
                          sx={{
                            color: voiceAssistant.status === 'listening' ? 'error.main' : 'primary.main',
                            bgcolor: alpha(theme.palette.primary.main, 0.08),
                            width: 38,
                            height: 38,
                            borderRadius: '10px',
                            '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.15) },
                          }}
                        >
                          <MicIcon sx={{ fontSize: 20 }} />
                        </IconButton>
                      </>
                    )}

                    {/* Pantalla Completa / Modo POS toggle (Desktop) */}
                    {!isMobile && (
                      <Tooltip title={isAppMode ? "Salir de Pantalla Completa (F11)" : "Pantalla Completa / Modo POS (F11)"} arrow>
                        <IconButton
                          onClick={() => togglePageFullscreen()}
                          sx={{
                            color: isAppMode ? 'primary.main' : 'text.secondary',
                            bgcolor: isAppMode ? alpha(theme.palette.primary.main, 0.12) : alpha(theme.palette.action.hover, 0.05),
                            width: 44,
                            height: 44,
                            borderRadius: '12px',
                            transition: 'all 0.2s',
                            '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.15), color: 'primary.main', transform: 'scale(1.05)' },
                          }}
                        >
                          {isAppMode ? <FullscreenExitIcon sx={{ fontSize: 22 }} /> : <FullscreenIcon sx={{ fontSize: 22 }} />}
                        </IconButton>
                      </Tooltip>
                    )}

                    {/* Monitor de Impresión Automática Local / Remota */}
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <PrintQueueMonitor />
                    </Box>

                    <IconButton
                      onClick={() => startTransition(() => setOpenNotificaciones(true))}
                      sx={{
                        color: 'text.secondary',
                        bgcolor: alpha(theme.palette.action.hover, 0.05),
                        width: { xs: 38, md: 44 },
                        height: { xs: 38, md: 44 },
                        borderRadius: { xs: '10px', md: '12px' },
                        '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.08), color: 'error.main', transform: 'translateY(-2px)' },
                        transition: 'all 0.25s',
                      }}
                    >
                      <Badge badgeContent={notificacionesNoLeidas} color="error" overlap="circular">
                        <NotificationsIcon sx={{ fontSize: { xs: 20, md: 22 } }} />
                      </Badge>
                    </IconButton>
                    <NotificacionesModal open={openNotificaciones} onClose={() => setOpenNotificaciones(false)} />

                    <Box
                      onClick={handleMenuOpen}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        cursor: 'pointer',
                        py: 0.5,
                        px: { xs: 0.4, md: 0.8 },
                        borderRadius: '40px',
                        bgcolor: alpha(theme.palette.background.paper, 0.6),
                        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          bgcolor: alpha(theme.palette.primary.main, 0.05),
                          borderColor: alpha(theme.palette.primary.main, 0.2),
                          boxShadow: '0 4px 15px rgba(0,0,0,0.05)',
                        },
                      }}
                    >
                      <Avatar
                        src={user.fotoUrl}
                        sx={{
                          width: { xs: 34, md: 40 },
                          height: { xs: 34, md: 40 },
                          boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
                          transition: 'transform 0.3s ease',
                          '&:hover': { transform: 'scale(1.1)' },
                        }}
                      >
                        {user.nombre?.[0] || user.email?.[0]}
                      </Avatar>
                      <Box sx={{ display: { xs: 'none', lg: 'block' }, pr: 1.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 800, color: 'text.primary', fontSize: '0.85rem' }}>
                          {user.nombre || user.email?.split('@')[0]}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 800, fontSize: '0.62rem', letterSpacing: 0.5 }}>
                          EN LÍNEA 🟢
                        </Typography>
                      </Box>
                    </Box>

                    <Menu
                      anchorEl={anchorEl}
                      open={Boolean(anchorEl)}
                      onClose={handleMenuClose}
                      onClick={handleMenuClose}
                      PaperProps={{
                        sx: {
                          mt: { xs: 0, md: 2 },
                          mb: { xs: 2, md: 0 },
                          width: 240,
                          borderRadius: 4,
                          boxShadow: '0 15px 40px rgba(0,0,0,0.15)',
                          p: 1,
                          '& .MuiMenuItem-root': {
                            py: 1.5,
                            px: 2,
                            borderRadius: 2,
                            fontWeight: 600,
                            fontSize: '0.88rem',
                            '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.05), color: 'primary.main' },
                          },
                        },
                      }}
                      transformOrigin={{ horizontal: 'right', vertical: isMobile ? 'bottom' : 'top' }}
                      anchorOrigin={{ horizontal: 'right', vertical: isMobile ? 'top' : 'bottom' }}
                    >
                      <MenuItem onClick={handleProfile}><ListItemIcon><AccountCircleIcon /></ListItemIcon> Mi Cuenta 👤</MenuItem>
                      <MenuItem onClick={() => startTransition(() => navigate('/settings'))}><ListItemIcon><SettingsIcon /></ListItemIcon> Ajustes ⚙️</MenuItem>
                      <MenuItem onClick={() => startTransition(() => setOpenSoporte(true))}><ListItemIcon><SupportAgentIcon /></ListItemIcon> Soporte 🎧</MenuItem>
                      <Divider sx={{ my: 1, opacity: 0.5 }} />
                      <MenuItem onClick={handleLogout} sx={{ color: 'error.main', '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.08) } }}>
                        <ListItemIcon><LogoutIcon color="error" /></ListItemIcon> Salir 👋
                      </MenuItem>
                    </Menu>
                  </>
                )}
              </Stack>
            </>
          )}

          {/* ── Dropdown results ── */}
          <Popper
            open={Boolean(searchAnchorEl) && searchQuery.length >= 2}
            anchorEl={searchAnchorEl}
            placement={isMobile ? "top-start" : "bottom-start"}
            style={{ zIndex: 1400, width: searchAnchorEl?.offsetWidth || 620 }}
          >
            <ClickAwayListener onClickAway={(e) => {
              if (searchAnchorEl && searchAnchorEl.contains(e.target)) {
                return;
              }
              setSearchAnchorEl(null);
            }}>
              <Paper
                elevation={0}
                sx={{
                  mt: isMobile ? 0 : 1.5,
                  mb: isMobile ? 1.5 : 0,
                  borderRadius: 3,
                  boxShadow: '0 15px 45px rgba(0,0,0,0.12)',
                  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  bgcolor: alpha(theme.palette.background.paper, 0.98),
                  backdropFilter: 'blur(12px)',
                  overflow: 'hidden',
                }}
              >
                <List sx={{ py: 0, maxHeight: 480, overflowY: 'auto' }}>
                  {isSearching && (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                      <CircularProgress size={28} />
                    </Box>
                  )}

                  {/* Navigation shortcuts */}
                  {navigationResults.length > 0 && (
                    <Box sx={{ p: 1 }}>
                      <Typography variant="caption" sx={{ px: 2, py: 0.5, display: 'block', fontWeight: 800, color: 'primary.main', textTransform: 'uppercase', fontSize: '0.65rem' }}>
                        📍 ACCESOS RÁPIDOS
                      </Typography>
                      {navigationResults.map((item, idx) => (
                        <MenuItem key={idx} onClick={() => handleResultClick(item.path)} sx={{ borderRadius: 2, mb: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 38, color: 'primary.main' }}>
                            <DashboardIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary={item.title}
                            primaryTypographyProps={{ fontWeight: 600, fontSize: '0.88rem' }}
                          />
                        </MenuItem>
                      ))}
                    </Box>
                  )}

                  {/* Products */}
                  {!isSearching && searchResults.products?.length > 0 && (
                    <Box sx={{ p: 1 }}>
                      <Divider sx={{ my: 0.5 }} />
                      <Typography variant="caption" sx={{ px: 2, py: 0.5, display: 'block', fontWeight: 800, color: 'success.main', textTransform: 'uppercase', fontSize: '0.65rem' }}>
                        📦 PRODUCTOS
                      </Typography>
                      {searchResults.products.map((p) => (
                        <MenuItem
                          key={p._id || p.id}
                          onClick={() => handleProductClick(p)}
                          sx={{ borderRadius: 2, mb: 0.5 }}
                        >
                          <ListItemIcon sx={{ minWidth: 38 }}>
                            <InventoryIcon fontSize="small" color="success" />
                          </ListItemIcon>
                          <ListItemText
                            primary={p.nombre}
                            secondary={`RD$ ${p.precio} · Stock: ${p.stock ?? '–'}`}
                            primaryTypographyProps={{ fontWeight: 600, fontSize: '0.88rem' }}
                          />
                          <Chip
                            icon={<AddCartIcon sx={{ fontSize: '14px !important' }} />}
                            label="Agregar"
                            size="small"
                            color="success"
                            variant="outlined"
                            onClick={(e) => { e.stopPropagation(); handleAddToCart(p); }}
                            sx={{ borderRadius: 2, fontWeight: 700, fontSize: '0.7rem' }}
                          />
                        </MenuItem>
                      ))}
                    </Box>
                  )}

                  {/* Customers */}
                  {!isSearching && searchResults.customers?.length > 0 && (
                    <Box sx={{ p: 1 }}>
                      <Divider sx={{ my: 0.5 }} />
                      <Typography variant="caption" sx={{ px: 2, py: 0.5, display: 'block', fontWeight: 800, color: 'info.main', textTransform: 'uppercase', fontSize: '0.65rem' }}>
                        👥 CLIENTES
                      </Typography>
                      {searchResults.customers.map((c) => (
                        <MenuItem
                          key={c._id || c.id}
                          onClick={() => handleResultClick(`/customers/${c._id || c.id}`)}
                          sx={{ borderRadius: 2, mb: 0.5 }}
                        >
                          <ListItemIcon sx={{ minWidth: 38 }}>
                            <PeopleIcon fontSize="small" color="info" />
                          </ListItemIcon>
                          <ListItemText
                            primary={c.nombre || c.name}
                            secondary={c.email || c.telefono || 'Cliente'}
                            primaryTypographyProps={{ fontWeight: 600, fontSize: '0.88rem' }}
                          />
                        </MenuItem>
                      ))}
                    </Box>
                  )}

                  {/* No results */}
                  {!isSearching && searchResults.products.length === 0 && searchResults.customers.length === 0 && navigationResults.length === 0 && (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                      <Typography color="text.secondary" fontSize="0.88rem">
                        No se encontraron resultados para "{searchQuery}"
                      </Typography>
                    </Box>
                  )}
                </List>
              </Paper>
            </ClickAwayListener>
          </Popper>
        </Toolbar>
      </AppBar>

      <Navigation
        mobileOpen={mobileOpen}
        onClose={handleDrawerToggle}
        drawerWidth={drawerFullWidth}
        isAppMode={isAppMode}
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={toggleSidebarCollapse}
      />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          width: '100%',
          overflowX: 'hidden',
          overflowY: isAppMode ? 'hidden' : 'auto',
          p: isAppMode ? { xs: 1, md: 1.5 } : { xs: 1.5, sm: 2, md: 2.5, lg: 3 },
          pt: isAppMode
            ? { xs: 'calc(10px + env(safe-area-inset-top, 0px))', md: '10px' }
            : { xs: 'calc(16px + env(safe-area-inset-top, 0px))', sm: 'calc(20px + env(safe-area-inset-top, 0px))', md: 2.5, lg: 3 },
          pb: isAppMode
            ? { xs: 'calc(80px + env(safe-area-inset-bottom, 0px))', md: '10px' }
            : { xs: 'calc(90px + env(safe-area-inset-bottom, 0px))', sm: 'calc(96px + env(safe-area-inset-bottom, 0px))', md: 3, lg: 4 },
          mt: { xs: 0, md: 'calc(80px + env(safe-area-inset-top, 0px))' },
          bgcolor: 'background.default',
          transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
          minHeight: isAppMode ? { xs: 'calc(100vh - 72px)', md: 'calc(100vh - 80px)' } : '100vh',
          height: isAppMode ? { xs: 'auto', md: 'calc(100vh - 80px)' } : 'auto',
          maxHeight: isAppMode ? { xs: 'none', md: 'calc(100vh - 80px)' } : 'none',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Routes>
          <Route path="/canva-flyer" element={<CanvaFlyerGenerator />} />
        </Routes>
        <Box
          key={location.pathname}
          sx={{
            width: '100%',
            maxWidth: '100%',
            minWidth: 0,
            flex: 1,
            minHeight: 0,
            overflow: isAppMode ? 'hidden' : 'visible',
            animation: 'pageFadeIn 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
            '@keyframes pageFadeIn': {
              '0%': { opacity: 0, transform: 'translateY(6px)' },
              '100%': { opacity: 1, transform: 'translateY(0)' },
            },
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {children}
        </Box>
      </Box>

      <SoporteTecnicoModal open={openSoporte} onClose={() => setOpenSoporte(false)} />

      {/* Pre-order dialog for products found in search */}
      <PreOrderDialog
        product={preOrderProduct}
        open={preOrderOpen}
        onClose={() => { setPreOrderOpen(false); setPreOrderProduct(null); }}
        onAddToCart={handleAddToCart}
        onNavigate={(path) => { startTransition(() => navigate(path)); }}
      />

      <ProductDetailModal
        open={Boolean(selectedProduct)}
        onClose={() => setSelectedProduct(null)}
        product={selectedProduct}
      />
    </Box>
  );
};

export default Layout;
